import type { ModelLoader } from '../model/ModelLoader';
import type { UserModelStore } from '../model/UserModelStore';
import type { ModelManifestEntry } from '../model/ModelConfig';

export interface ModelSelectorCallbacks {
  onSelectBuiltin: (modelId: string) => void;
  onSelectUserModel: (modelId: string) => void;
  onUploadUserModel: (files: FileList) => void;
  onDeleteUserModel: (modelId: string) => void;
}

export class ModelSelector {
  readonly el: HTMLDivElement;
  private callbacks: ModelSelectorCallbacks;
  private userModelListEl: HTMLDivElement;
  private builtinModelListEl: HTMLDivElement;
  private modelLoader: ModelLoader;
  private userModelStore: UserModelStore;
  private uploadBtn: HTMLButtonElement;
  private fileInput: HTMLInputElement;

  constructor(
    container: HTMLElement,
    modelLoader: ModelLoader,
    userModelStore: UserModelStore,
    callbacks: ModelSelectorCallbacks,
  ) {
    this.modelLoader = modelLoader;
    this.userModelStore = userModelStore;
    this.callbacks = callbacks;

    this.el = document.createElement('div');
    this.el.className = 'model-selector';

    const builtinTitle = document.createElement('h3');
    builtinTitle.textContent = 'Built-in Models';
    this.el.appendChild(builtinTitle);

    this.builtinModelListEl = document.createElement('div');
    this.builtinModelListEl.className = 'model-list';
    this.el.appendChild(this.builtinModelListEl);

    const userTitle = document.createElement('h3');
    userTitle.textContent = 'User Models';
    this.el.appendChild(userTitle);

    this.userModelListEl = document.createElement('div');
    this.userModelListEl.className = 'model-list';
    this.el.appendChild(this.userModelListEl);

    const uploadSection = document.createElement('div');
    uploadSection.className = 'upload-section';

    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.multiple = true;
    this.fileInput.accept = '.json,.bin,.png';
    uploadSection.appendChild(this.fileInput);

    this.uploadBtn = document.createElement('button');
    this.uploadBtn.textContent = 'Upload Model';
    this.uploadBtn.onclick = () => {
      if (this.fileInput.files && this.fileInput.files.length > 0) {
        this.callbacks.onUploadUserModel(this.fileInput.files);
        this.fileInput.value = '';
      }
    };
    uploadSection.appendChild(this.uploadBtn);

    const uploadHint = document.createElement('span');
    uploadHint.className = 'upload-hint';
    uploadHint.textContent = 'Select model.json + rom.bin + (optional) interface.png';
    uploadSection.appendChild(uploadHint);

    this.el.appendChild(uploadSection);

    container.appendChild(this.el);
  }

  async refresh(): Promise<void> {
    await this.loadBuiltinModels();
    await this.loadUserModels();
  }

  private async loadBuiltinModels(): Promise<void> {
    this.builtinModelListEl.innerHTML = '';
    try {
      const models = await this.modelLoader.getBuiltinModels();
      for (const model of models) {
        this.builtinModelListEl.appendChild(this.createBuiltinRow(model));
      }
    } catch (err) {
      this.builtinModelListEl.textContent = 'Failed to load built-in models';
    }
  }

  private async loadUserModels(): Promise<void> {
    this.userModelListEl.innerHTML = '';
    try {
      const models = await this.userModelStore.listStoredUserModels();
      if (models.length === 0) {
        this.userModelListEl.textContent = 'No user models stored';
        return;
      }
      for (const model of models) {
        this.userModelListEl.appendChild(this.createUserRow(model));
      }
    } catch (err) {
      this.userModelListEl.textContent = 'Failed to load user models';
    }
  }

  private createBuiltinRow(model: ModelManifestEntry): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'model-row';

    const nameSpan = document.createElement('span');
    nameSpan.textContent = model.name + ' (' + model.hardwareGeneration + ')';
    row.appendChild(nameSpan);

    const loadBtn = document.createElement('button');
    loadBtn.textContent = 'Load';
    loadBtn.onclick = () => this.callbacks.onSelectBuiltin(model.id);
    row.appendChild(loadBtn);

    return row;
  }

  private createUserRow(model: ModelManifestEntry): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'model-row';

    const nameSpan = document.createElement('span');
    nameSpan.textContent = model.name + ' (' + model.hardwareGeneration + ')';
    row.appendChild(nameSpan);

    const loadBtn = document.createElement('button');
    loadBtn.textContent = 'Load';
    loadBtn.onclick = () => this.callbacks.onSelectUserModel(model.id);
    row.appendChild(loadBtn);

    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = 'Delete';
    deleteBtn.className = 'delete-btn';
    deleteBtn.onclick = () => {
      if (confirm('Delete model "' + model.name + '"? This action cannot be undone.')) {
        this.callbacks.onDeleteUserModel(model.id);
        deleteBtn.disabled = true;
        deleteBtn.textContent = 'Deleting...';
      }
    };
    row.appendChild(deleteBtn);

    return row;
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
